// src/components/games/pipsWordPuzzle/PipsWordPuzzle.tsx
import { logger } from '../../../utils/logger';
// FIXED: Removed Unicode backspace character that was causing keyboard corruption

import React, { useState, useEffect, useRef } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonButton,
  IonModal,
  IonIcon,
  IonInput
} from '@ionic/react';
import {
  shareOutline,
  chevronDown,
  chevronUp,
  star
} from 'ionicons/icons';
import { useLocation, useHistory } from 'react-router-dom';
import { GameState, GuessResult, GameStats, DailyPuzzle } from './types';
import { getTodaysPuzzle } from './puzzles';
import { checkGuess, isValidWord, isValidWordAsync, calculatePoints, getKeyboardStatus, KEYBOARD_ROWS } from './gameLogic';
import { preloadCommonWords } from './dictionaryService';
import {
  getStats,
  saveStats,
  getGameState,
  saveGameState,
  updateStats,
  clearGameState,
  markPuzzleCompleted,
  isPuzzleCompleted,
  migrateLocalStatsToDatabase,
  submitLeaderboardScore
} from '../../../services/pipsWordPuzzleStorageService';
import { soundService } from '../../../services/soundService';
import './PipsWordPuzzle.css';

const MAX_GUESSES = 6;
const USE_NATIVE_KEYBOARD = false; // Toggle this to use native keyboard

interface LocationState {
  puzzle?: DailyPuzzle;
  fromArchive?: boolean;
}

export const PipsWordPuzzle: React.FC = () => {
  const location = useLocation<LocationState>();
  const history = useHistory();
  const archivePuzzle = location.state?.puzzle;
  const fromArchive = location.state?.fromArchive || false;

  const [puzzle, setPuzzle] = useState<DailyPuzzle | null>(null);
  const [stats, setStats] = useState<GameStats>({
    gamesPlayed: 0,
    gamesWon: 0,
    currentStreak: 0,
    maxStreak: 0,
    points: 15,
    guessDistribution: [0, 0, 0, 0, 0, 0]
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [currentGuess, setCurrentGuess] = useState('');
  const [guesses, setGuesses] = useState<GuessResult[]>([]);
  const [gameStatus, setGameStatus] = useState<'playing' | 'won' | 'lost'>('playing');
  const [hintsRevealed, setHintsRevealed] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);

  // Hints expansion state
  const [hintsExpanded, setHintsExpanded] = useState(false);
  const hiddenInputRef = useRef<HTMLIonInputElement>(null);

  // Load stats from database on mount
  useEffect(() => {
    // Load Pip's Word Puzzle sounds
    soundService.loadSounds({
      'letter-correct': '/assets/sounds/pips-word-puzzle/correct.mp3',
      'letter-wrong': '/assets/sounds/pips-word-puzzle/wrong.mp3',
      'word-submit': '/assets/sounds/pips-word-puzzle/submit.mp3',
      'game-win': '/assets/sounds/pips-word-puzzle/win.mp3',
    });

    const loadStats = async () => {
      setStatsLoading(true);

      try {
        // Try to migrate old localStorage stats first (will do nothing now)
        await migrateLocalStatsToDatabase();

        // Load stats from database
        const dbStats = await getStats();
        setStats(dbStats);
      } catch (error) {
        logger.error("Failed to load Pip's Word Puzzle stats:", error);
        // Keep default stats if loading fails
      } finally {
        setStatsLoading(false);
      }
    };

    loadStats();
  }, []);

  // Load puzzle on mount - either today's or from archive
  useEffect(() => {
    const loadPuzzle = async () => {
      setLoading(true);

      try {
        // If coming from archive with a specific puzzle, use that
        if (archivePuzzle) {
          setPuzzle(archivePuzzle);

          // Preload dictionary with this puzzle word
          preloadCommonWords(archivePuzzle.word);

          // Check if this archive puzzle was already completed
          const wasCompleted = await isPuzzleCompleted(archivePuzzle.date, archivePuzzle.word);
          setAlreadyCompleted(wasCompleted);

          if (wasCompleted) {
            // Show completion message
            showMessage('You already completed this puzzle!');
          }

          // Archive puzzles always start fresh in terms of game state
          await clearGameState();
          setGuesses([]);
          setGameStatus('playing');
          setHintsRevealed([]);
          setCurrentGuess('');
        } else {
          // Load today's puzzle
          const todaysPuzzle = await getTodaysPuzzle();
          setPuzzle(todaysPuzzle);
          setAlreadyCompleted(false);

          // Preload dictionary with today's puzzle word (most important!)
          preloadCommonWords(todaysPuzzle.word);

          // Check for saved state AFTER puzzle is loaded
          const savedState = await getGameState();
          if (savedState && savedState.puzzleDate === todaysPuzzle.date) {
            // Restore saved state for this puzzle
            setGuesses(savedState.guesses);
            setGameStatus(savedState.gameStatus);
            setHintsRevealed(savedState.hintsRevealed);
            setCurrentGuess('');
            if (savedState.gameStatus !== 'playing') {
              setShowModal(true);
            }
          } else {
            // Clear any old saved state
            if (savedState) {
              await clearGameState();
            }
            // Start fresh
            setGuesses([]);
            setGameStatus('playing');
            setHintsRevealed([]);
            setCurrentGuess('');
          }
        }
      } catch (error) {
        logger.error("Failed to load Pip's Word Puzzle puzzle:", error);
        // Set a default puzzle on error
        setPuzzle(null);
      } finally {
        setLoading(false);
      }
    };

    loadPuzzle();
  }, [archivePuzzle]);

  // Save game state whenever it changes (only for today's puzzle, not archive)
  useEffect(() => {
    if (puzzle && !fromArchive && (guesses.length > 0 || hintsRevealed.length > 0)) {
      const state: GameState = {
        currentGuess,
        guesses,
        gameStatus,
        hintsRevealed,
        pointsEarned: 0,
        pointsSpent: hintsRevealed.length * 5,
        puzzleDate: puzzle.date
      };
      // Save is now async but we don't await it to avoid blocking
      saveGameState(state);
    }
  }, [currentGuess, guesses, gameStatus, hintsRevealed, puzzle, fromArchive]);

  const handleKeyPress = (key: string) => {
    if (gameStatus !== 'playing' || !puzzle) return;

    if (key === 'ENTER') {
      submitGuess();
    } else if (key === 'BACK') {
      setCurrentGuess(prev => prev.slice(0, -1));
    } else if (currentGuess.length < puzzle.word.length) {
      setCurrentGuess(prev => prev + key);
    }
  };

  // Handle native keyboard input
  const handleNativeInput = (e: any) => {
    if (!puzzle || gameStatus !== 'playing') return;

    const value = e.detail.value?.toUpperCase() || '';

    // Only allow letters and respect max length
    const filtered = value.replace(/[^A-Z]/g, '').slice(0, puzzle.word.length);
    setCurrentGuess(filtered);
  };

  // Handle native keyboard enter key
  const handleNativeKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      submitGuess();
    }
  };

  const submitGuess = async () => {
    if (!puzzle) return;

    if (currentGuess.length !== puzzle.word.length) {
      showMessage('Not enough letters');
      return;
    }

    // Use async validation with Supabase (falls back to local if unavailable)
    const isValid = await isValidWordAsync(currentGuess);
    if (!isValid) {
      showMessage('Invalid word');
      soundService.play('letter-wrong', { volume: 0.4, haptic: 'light' });
      return;
    }

    const result = checkGuess(currentGuess, puzzle.word);
    const newGuesses = [...guesses, result];
    setGuesses(newGuesses);
    setCurrentGuess('');

    // Play submit sound
    soundService.play('word-submit', { volume: 0.5, haptic: 'light' });

    // Check if won
    if (currentGuess.toUpperCase() === puzzle.word.toUpperCase()) {
      // Play win sound
      soundService.play('game-win', { volume: 0.7, haptic: 'success' });
      // Only award points if this is the first time completing this puzzle
      if (!alreadyCompleted) {
        const pointsEarned = calculatePoints(true, newGuesses.length, hintsRevealed.length);
        const pointsSpent = hintsRevealed.length * 5;
        const netPoints = pointsEarned - pointsSpent;

        const newStats = updateStats(stats, true, newGuesses.length, netPoints);
        setStats(newStats);
        await saveStats(newStats);

        // Submit to leaderboard
        await submitLeaderboardScore(
          puzzle.word,
          puzzle.date,
          newGuesses.length,
          hintsRevealed.length,
          true,
          newStats.points
        );

        // Mark puzzle as completed with word
        await markPuzzleCompleted(puzzle.date, puzzle.word);
      }

      setGameStatus('won');
      setShowModal(true);
    } else if (newGuesses.length >= MAX_GUESSES) {
      // Lost - only update stats if first time playing this puzzle
      if (!alreadyCompleted) {
        const newStats = updateStats(stats, false, newGuesses.length, 0);
        setStats(newStats);
        await saveStats(newStats);

        // Submit to leaderboard (even when lost)
        await submitLeaderboardScore(
          puzzle.word,
          puzzle.date,
          newGuesses.length,
          hintsRevealed.length,
          false,
          newStats.points
        );
      }

      setGameStatus('lost');
      setShowModal(true);
    }
  };

  const revealHint = async (index: number) => {
    if (stats.points < 5 || hintsRevealed.includes(index)) return;

    // Deduct points and reveal hint
    const newStats = { ...stats, points: stats.points - 5 };
    setStats(newStats);
    await saveStats(newStats);

    setHintsRevealed(prev => [...prev, index]);
  };

  const showMessage = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 2000);
  };

  const shareResults = async () => {
    if (!puzzle) return;

    const gridEmojis = guesses.map(guess =>
      guess.letters.map(letter => {
        switch (letter.status) {
          case 'correct': return '🟩';
          case 'present': return '🟨';
          default: return '⬜';
        }
      }).join('')
    ).join('\n');

    const shareText = `Pip's Word Puzzle ${puzzle.date}\n${guesses.length}/${MAX_GUESSES}\n\n${gridEmojis}`;

    if (navigator.share) {
      try {
        await navigator.share({
          text: shareText
        });
      } catch (err) {
        // Fallback to clipboard
        await navigator.clipboard.writeText(shareText);
        showMessage('Results copied to clipboard!');
      }
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(shareText);
      showMessage('Results copied to clipboard!');
    }
  };

  const keyboardStatus = getKeyboardStatus(guesses);

  const renderGuessRows = () => {
    const rows = [];

    // Render completed guesses
    for (let i = 0; i < guesses.length; i++) {
      const guess = guesses[i];
      rows.push(
        <div key={i} className="guess-row">
          {guess.letters.map((letter, j) => (
            <div key={j} className={`letter-tile ${letter.status}`}>
              {letter.letter}
              {letter.status === 'correct' && (
                <span className="cat-paw-overlay">🐾</span>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Render current guess row
    if (gameStatus === 'playing' && guesses.length < MAX_GUESSES && puzzle) {
      const currentRow = [];
      for (let j = 0; j < puzzle.word.length; j++) {
        const letter = currentGuess[j] || '';
        currentRow.push(
          <div key={j} className={`letter-tile ${letter ? 'filled' : 'empty'}`}>
            {letter}
          </div>
        );
      }
      rows.push(
        <div key={guesses.length} className="guess-row">
          {currentRow}
        </div>
      );
    }

    // Render empty rows
    const remainingRows = MAX_GUESSES - rows.length;
    for (let i = 0; i < remainingRows; i++) {
      rows.push(
        <div key={guesses.length + 1 + i} className="guess-row">
          {Array.from({ length: puzzle?.word.length || 5 }, (_, j) => (
            <div key={j} className="letter-tile empty"></div>
          ))}
        </div>
      );
    }

    return rows;
  };

  if (loading || statsLoading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton
                defaultHref={fromArchive ? "/games/pips-word-puzzle/archive" : "/games/pips-word-puzzle"}
              />
            </IonButtons>
            <IonTitle>Pip's Word Puzzle</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading puzzle...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!puzzle) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton
                defaultHref={fromArchive ? "/games/pips-word-puzzle/archive" : "/games/pips-word-puzzle"}
              />
            </IonButtons>
            <IonTitle>Pip's Word Puzzle</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div className="error-container">
            <p>Failed to load puzzle</p>
            <IonButton onClick={() => history.push('/games/pips-word-puzzle')}>
              Back to Menu
            </IonButton>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton
              defaultHref={fromArchive ? "/games/pips-word-puzzle/archive" : "/games/pips-word-puzzle"}
            />
          </IonButtons>
          <IonTitle>
            {fromArchive ? `Archive: ${puzzle.date}` : 'Today\'s Nerdle'}
          </IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div className="pips-word-puzzle-container">
          {/* Points Display */}
          <div className="points-display">
            <IonIcon icon={star} className="points-icon" />
            <span className="points-value">{stats.points}</span>
            <span className="points-label">points</span>
          </div>

          {/* Message Toast */}
          {message && (
            <div className="message-toast">
              {message}
            </div>
          )}

          {/* Guess Grid */}
          <div className="guess-grid">
            {renderGuessRows()}
          </div>

          {/* Hints Section - From Pip! */}
          {puzzle.hints && puzzle.hints.length > 0 && (
            <div className="hints-section">
              <div
                className="hints-header"
                onClick={() => setHintsExpanded(!hintsExpanded)}
              >
                <span className="hints-title">
                  🐱 Pip's Hints ({puzzle.hints.length})
                </span>
                <IonIcon icon={hintsExpanded ? chevronUp : chevronDown} />
              </div>

              {hintsExpanded && (
                <div className="hints-list">
                  {puzzle.hints.map((hint, index) => (
                    <div key={index} className="hint-item">
                      {hintsRevealed.includes(index) ? (
                        <div className="hint-revealed">
                          <span className="hint-icon">🐾</span>
                          <span className="hint-text">{hint.text}</span>
                        </div>
                      ) : (
                        <div className="hint-locked">
                          <span className="hint-icon">🔒</span>
                          <span className="hint-text">Pip has a hint for you!</span>
                          <IonButton
                            size="small"
                            onClick={() => revealHint(index)}
                            disabled={stats.points < 5 || gameStatus !== 'playing'}
                          >
                            Ask Pip (5 pts)
                          </IonButton>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Hidden Input for Native Keyboard */}
          {USE_NATIVE_KEYBOARD && gameStatus === 'playing' && (
            <IonInput
              ref={hiddenInputRef}
              value={currentGuess}
              onIonInput={handleNativeInput}
              onKeyDown={handleNativeKeyDown}
              maxlength={puzzle.word.length}
              className="hidden-input"
              autofocus
            />
          )}

          {/* FIXED: Keyboard with proper BACK button rendering */}
          {!USE_NATIVE_KEYBOARD && (
            <div className="keyboard">
              {KEYBOARD_ROWS.map((row, i) => (
                <div key={i} className="keyboard-row">
                  {row.map(key => (
                    <button
                      key={key}
                      className={`keyboard-key ${keyboardStatus.get(key) || ''} ${
                        key === 'ENTER' || key === 'BACK' ? 'wide' : ''
                      }`}
                      onClick={() => handleKeyPress(key)}
                      disabled={gameStatus !== 'playing'}
                    >
                      {key === 'BACK' ? 'DEL' : key}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Game Over Modal */}
        <IonModal
          isOpen={showModal}
          onDidDismiss={() => setShowModal(false)}
        >
          <IonContent className="ion-padding">
            <div style={{ textAlign: 'center', paddingTop: '40px' }}>
              <h2 style={{ fontSize: '32px', marginBottom: '20px' }}>
                {gameStatus === 'won' ? 'YOU WON!' : 'GAME OVER'}
              </h2>

              {gameStatus === 'won' ? (
                <div style={{ marginBottom: '30px' }}>
                  <p style={{ fontSize: '18px', marginBottom: '12px' }}>
                    The word was: <strong>{puzzle.word}</strong>
                  </p>
                  <p style={{ marginBottom: '8px' }}>Guesses: {guesses.length}/{MAX_GUESSES}</p>
                  {!alreadyCompleted && (
                    <>
                      <p style={{ marginBottom: '8px', color: 'var(--ion-color-success)' }}>
                        Points earned: +{calculatePoints(true, guesses.length, hintsRevealed.length)}
                      </p>
                      {hintsRevealed.length > 0 && (
                        <p style={{ marginBottom: '8px', color: 'var(--ion-color-danger)' }}>
                          Points spent: -{hintsRevealed.length * 5}
                        </p>
                      )}
                    </>
                  )}
                  {alreadyCompleted && (
                    <p style={{ marginTop: '16px', color: 'var(--ion-color-warning)', fontStyle: 'italic' }}>
                      [WARNING] Already completed - no points awarded
                    </p>
                  )}
                </div>
              ) : (
                <div style={{ marginBottom: '30px' }}>
                  <p style={{ fontSize: '18px', marginBottom: '12px' }}>
                    The word was: <strong>{puzzle.word}</strong>
                  </p>
                  <p>Better luck next time!</p>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '400px', margin: '0 auto' }}>
                <IonButton
                  expand="block"
                  onClick={() => {
                    setShowModal(false);
                    history.push('/games/pips-word-puzzle');
                  }}
                >
                  Back to Menu
                </IonButton>
              </div>
            </div>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};